import TenantAPI from "api/api/TenantAPI";
import {
  useState,
  useEffect,
  useRef,
  forwardRef,
  useImperativeHandle,
} from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useAtomValue } from "jotai";

import useAllTags from "hooks/useAllTags";
import SelectableInput from "components/SelectableInput/SelectableInput";
import StrUtils from "utils/StrUtils";
import SaveTagDrawer from "components/Tags/SaveTagDrawer";
import useUser from "api/api-hooks/auth/useUser";
import { handleUpdatedRecords } from "utils/RecordUtils";
import EmptyField from "./EmptyField";
import { tenantIDAtom } from "../../atoms/GlobalAtoms";

const ManageRecordTags = (
  {
    table,
    moduleAPIName,
    recordId,
    recordTagIds,
    full,
    mode,
    modifyTrigger,
    display, // Prop used to render empty tags as "--" in table and as "No Tags" in ViewRecordDrawer
    subitemAPIName,
    multiSubitemID,
    disabled,
  },
  ref
) => {
  const inputRef = useRef();
  const { user } = useUser();
  const { tagsLoading, tags: allTags, getTags } = useAllTags(moduleAPIName);
  const [tagsDrawerInDom, setTagsDrawerInDom] = useState(false);
  const [tagsDrawerOpen, setTagsDrawerOpen] = useState(false);
  const [editingTags, setEditingTags] = useState(() => {
    if (tagsLoading) return [];

    return getTags(recordTagIds?.length ? recordTagIds : []);
  });
  const queryClient = useQueryClient();
  const [external, setExternal] = useState(false);
  const updateStack = useRef([]);
  const tenantID = useAtomValue(tenantIDAtom);

  useEffect(() => {
    if (tagsLoading) return;

    setEditingTags(getTags(recordTagIds?.length ? recordTagIds : []));
  }, [getTags, tagsLoading, recordTagIds]);

  const handleResult = (data, tempID) => {
    if (updateStack.current.length === 1 && updateStack.current[0] === tempID) {
      updateStack.current = [];

      handleUpdatedRecords(
        queryClient,
        moduleAPIName,
        data?.data?.data?.Record,
        tenantID ?? ""
      );
    } else {
      const foundIndex = updateStack.current.indexOf(tempID);
      updateStack.current.splice(foundIndex, 1);
    }
  };

  const updateRecordTags = (tagIDs, action) => {
    let tempID = StrUtils.uuidv4();
    updateStack.current.push(tempID);

    const payload = {
      Tags: tagIDs,
      Action: action,
      ...(subitemAPIName && { SubitemAPIName: subitemAPIName }),
      ...(multiSubitemID && { MultiSubitemID: multiSubitemID }),
    };

    TenantAPI.updateRecordTags(moduleAPIName, recordId, payload)
      .then((data) => handleResult(data, tempID))
      .catch((e) => console.error(e));
  };

  const handleSelect = (val) => {
    setEditingTags((prev) => [...prev, val]);
    updateRecordTags([val.id], "add");
  };

  const handleDeselect = (val) => {
    setEditingTags((prev) => prev.filter((t) => t?.id !== val.id));
    updateRecordTags([val.id], "remove");
  };

  const handleClear = async () => {
    const tagsToDelete = editingTags.map((t) => t.id);
    updateRecordTags(tagsToDelete, "remove");

    setEditingTags([]);
  };

  useImperativeHandle(ref, () => ({
    focus: () => inputRef.current.focus(),
  }));

  if (tagsLoading) {
    return (
      <div className="skeleton my-1" style={{ width: 200, height: 24 }}></div>
    );
  }

  return (
    <div
      className={`${full ? "" : "d-flex"} ${
        display !== "ViewRecordDrawer" ? "pr-2" : ""
      } flex-wrap`}
      style={{ width: display !== "ViewRecordDrawer" ? "100%" : "fit-content" }}
    >
      <SelectableInput
        ref={inputRef}
        table={table}
        options={allTags}
        empty={
          display === "ViewRecordDrawer" ? (
            <div className="font-weight-500 mr--2">No Tags +</div>
          ) : (
            <EmptyField table={table} />
          )
        }
        tooltipTrigger={display === "ViewRecordDrawer" ? "hover" : "none"}
        value={editingTags}
        groupBy={["Scope", "Type"]}
        groupByLabels={{
          Person: "Your Tags",
          Cloud: "Cloud Tags",
          Module: "Module Tags",
          Workspace: "Workspace Tags",
        }}
        field={{ Name: "Tags", APIName: "Tags" }}
        nestedGroupBy="TagGroup"
        labelKey="Name"
        valueKey="id"
        colorKey="Color"
        onSelect={handleSelect}
        onDeselect={handleDeselect}
        onClear={handleClear}
        multiselect={true}
        maxValuesDisplayed={3}
        inputDisplayMode={"subtle"}
        externalOpen={external}
        setExternalOpen={setExternal}
        mode={mode || "rw"}
        modifyTrigger={modifyTrigger}
        addableGroups={{
          "Your Tags": () => {
            setTagsDrawerOpen(true);
            setTagsDrawerInDom(true);
          },
        }}
        hiddenTemporarily={tagsDrawerInDom === true}
        disabled={disabled}
      />
      {tagsDrawerInDom && (
        <SaveTagDrawer
          open={tagsDrawerOpen}
          onClose={() => setTagsDrawerOpen(false)}
          onExited={() => setTagsDrawerInDom(false)}
          scope={{ Type: "Person", PersonID: user?.id }}
          fromSelectableInput
        />
      )}
    </div>
  );
};

export default forwardRef(ManageRecordTags);
